import type { Task } from "@common/types/task";
import type {
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import {
    buildBoardCells,
    getCellEntries,
} from "@design-system/Board/boardCells";
import type { TaskComparator } from "./taskBoardSort";

const getTaskId = ({ id }: Task) => id;

/** One lane's tasks within a column; the whole column when there are no lanes. */
export interface TaskBoardSection<TTask extends Task> {
    lane: BoardLaneDef | null;
    tasks: TTask[];
}

interface GroupTaskSectionsArgs<TTask extends Task> {
    tasks: readonly TTask[];
    columns: readonly BoardColumnDef[];
    lanes: readonly BoardLaneDef[] | undefined;
    getPlacements: (task: TTask) => readonly BoardPlacement[];
    compareTasks: TaskComparator;
}

/**
 * Each column's lanes in board order, each lane's tasks top to bottom as the
 * board shows them (the Board's own cells). A task with several copies in
 * one cell is listed there once.
 */
export const groupTaskSections = <TTask extends Task>({
    tasks,
    columns,
    lanes,
    getPlacements,
    compareTasks,
}: GroupTaskSectionsArgs<TTask>): Map<
    string,
    Array<TaskBoardSection<TTask>>
> => {
    const cells = buildBoardCells({
        items: tasks,
        getItemId: getTaskId,
        getPlacements,
        compareItems: compareTasks,
    });
    const sectionLanes = lanes ?? [null];
    return new Map(
        columns.map((column) => [
            column.id,
            sectionLanes.map((lane): TaskBoardSection<TTask> => ({
                lane,
                tasks: getCellEntries(cells, {
                    columnId: column.id,
                    laneId: lane?.id ?? null,
                }).map(({ item }) => item),
            })),
        ]),
    );
};

/** Each column's tasks in reading order, lane by lane, each task once. */
export const getTasksByColumnId = <TTask extends Task>(
    sectionsByColumnId: ReadonlyMap<
        string,
        ReadonlyArray<TaskBoardSection<TTask>>
    >,
): Map<string, TTask[]> =>
    new Map(
        [...sectionsByColumnId].map(([columnId, sections]) => [
            columnId,
            [...new Set(sections.flatMap((section) => section.tasks))],
        ]),
    );

/**
 * Task ids in board reading order for the detail's pager: column by column,
 * lane by lane, top to bottom, each task once (at its first copy).
 */
export const getTaskBoardNavigationIds = ({
    columns,
    tasksByColumnId,
}: {
    columns: readonly BoardColumnDef[];
    tasksByColumnId: ReadonlyMap<string, readonly Task[]>;
}): string[] => {
    const taskIds = columns.flatMap((column) =>
        (tasksByColumnId.get(column.id) ?? []).map(({ id }) => id),
    );
    return [...new Set(taskIds)];
};
